import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Skeleton, Typography } from '@mui/material';
import MssDataGridPro from '../../common/MssDataGridPro';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import { getData } from '../../../DataAccessLayer';
import { studentAnalytics } from '../../../DataAccessLayer/services';
import { COUNT_COLUMNS } from './columns';

const COLUMNS = [{ field: 'userId', headerName: 'Student', flex: 1 }, ...COUNT_COLUMNS];

/**
 * How many flashcard decks, quizzes and summaries each user generated in one
 * course, counting deleted ones and ones still generating but not failed ones,
 * with the course's totals pinned below.
 */
function StudentAnalytics() {
  const { courseId } = useParams();
  const { state } = useLocation();
  const [usage, setUsage] = useState({ users: [], totals: null });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setUsage(await getData(studentAnalytics(courseId)));
    } catch (err) {
      console.log(err);
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    load();
  }, [load]);

  // The course list passes its row's title and term. Without them, such as from
  // a bookmark, they come from the loaded rows, which all share the course's
  // title and term; rows from the dev portal have neither.
  const course = state ?? {
    courseTitle: usage.users.find((user) => user.courseTitle)?.courseTitle,
    termCode: usage.users.find((user) => user.termCode)?.termCode,
  };
  // Without the course list's title and term, the heading waits for the rows
  // rather than showing the course ID and replacing it once they load.
  const isHeadingLoading = !state && isLoading;

  return (
    <Box className="mss__analytics" id="studentAnalytics">
      <Breadcrumbs
        className="mss__breadcrumbs"
        id="studentAnalytics__breadcrumbs"
        aria-label="breadcrumb"
      >
        <BreadcrumbCrumb to="..">Analytics</BreadcrumbCrumb>
        <BreadcrumbCrumb>{courseId}</BreadcrumbCrumb>
      </Breadcrumbs>
      <Typography component="h1" className="analyticsTitle">
        {/* The course ID and term stand in for a course with no title. */}
        {isHeadingLoading ? (
          <Skeleton className="titleSkeleton" variant="text" />
        ) : (
          course.courseTitle || (
            <>
              Course {courseId}
              {course.termCode && ` · Term ${course.termCode}`}
            </>
          )
        )}
      </Typography>
      <Typography className="analyticsDescription">
        The number of flashcard decks, practice quizzes and summary notes each student created in
        this course. Includes deleted resources and resources still being created, but not ones that
        failed.
      </Typography>

      {loadError ? (
        <MssAlert id="studentAnalytics__loadError" severity="error" type="statusAlert">
          <MssApiError id="studentAnalytics__usage" widgetName="student analytics" reload={load} />
        </MssAlert>
      ) : (
        <MssDataGridPro
          id="studentAnalytics__dataGrid"
          className="mss__dataGrid"
          rows={usage.users}
          columns={COLUMNS}
          getRowId={(row) => row.userId}
          pinnedRows={{
            bottom: usage.totals ? [{ ...usage.totals, userId: 'Total' }] : [],
          }}
          isLoading={isLoading}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
            sorting: { sortModel: [{ field: 'total', sort: 'desc' }] },
          }}
          slots={{
            noRowsOverlay: () => <Box className="noRowsMessage">No resources generated yet.</Box>,
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'linear-progress' },
          }}
        />
      )}
    </Box>
  );
}

export default StudentAnalytics;
